import { parentDirectory } from "./directory-bands";
import type { ExplorerGraphPayload, ExplorerNodePayload } from "../../../shared/types";
import type { SymbolOrder } from "../../types";
import { getVisibleConnections, retainFile, type PinSet } from "../pin-state";
import { normalizeSymbolIdentifier } from "../symbolAnchors";
import { orderBranches, type BranchOrder, type ForwardReference, type OrderInput } from "./branch-order";
import { directoryState, NO_OPEN_DIRECTORIES, under, type OpenDirectories } from "./directory-state";
import { rankByNetworkSimplex, type Constraint } from "./network-simplex";
import type { PathResult } from "./state";
import { buildPathSubgraph, buildSelfLoopEdges } from "./subgraph-builder";
import type { LocalEdge, LocalSubgraph } from "./types";

/** A disclosed exploration, with every connection between its retained files. */
export interface BranchGraph {
  subgraph: LocalSubgraph;
  /** The files of each column, left to right, each column top to bottom in the chosen order. */
  columns: ExplorerNodePayload[][];
  hiddenConnections: Map<string, number>;
  relevantSymbols: Map<string, Set<string>>;
  /** The keys of the references that read against the columns: each cycle's feedback, drawn as stubs. */
  back: Set<string>;
  /** The rows each card shows, top to bottom, in the chosen symbol order; Internals last. */
  rows: Map<string, string[]>;
  /** The bands, lanes and crossings of the chosen order. */
  order: BranchOrder;
}

/** The ranking of retained files into columns, and the references the ranking reads backward. */
export interface BranchRanking {
  columns: ExplorerNodePayload[][];
  back: Set<string>;
}

/** The ranking's dials. */
export interface RankingOptions {
  /**
   * How strongly every file is pulled toward the last column, against the cost of the spans: zero ranks by the fewest
   * column spans alone; a weight above every pair's stands each file as far right as its consumers allow, which is the
   * longest-chain ranking the Local Map had before 2026-10-06.
   */
  pull?: number;
  /** Which column a file takes when several cost the same: the one with the fewest other cards (the rightmost among equals), the rightmost, or the leftmost. */
  tie?: "fewest" | "right" | "left";
}

/** Where the order step begins and how long it looks. */
export interface OrderOptions {
  /** How many left-and-right sweeps the ordering tries; four when omitted. */
  sweeps?: number;
  /** A seed for a shuffled starting order of each column; the ranking's order when omitted. */
  seed?: number;
  /** A starting order of each column, as a previous picture stood its files; it outranks the seed. */
  start?: readonly (readonly string[])[];
}

/** The dials of the layout's first two steps. */
export interface BranchOptions {
  /** An explicit path restricts the picture to its files and forward references between adjacent hops. */
  path?: PathResult;
  /** How a card's rows stand; the layout's own order when omitted. */
  symbolOrder?: SymbolOrder;
  ranking?: RankingOptions;
  order?: OrderOptions;
  /**
   * How many levels of directory below the retained files' common directory are membranes: files deeper belong to the
   * membrane at that level, so that at zero no directory shapes the order or the placement and the cards interleave
   * freely; null, the default, keeps every level. A lever of the layout lab (2026-10-07).
   */
  membraneDepth?: number | null;
  /**
   * The directories the person has opened: every file directly inside one joins the picture as a compact, unwired card
   * unless it is retained, and every immediate subdirectory neither opened nor encasing joins as a closed box
   * (the owner's grammar, 2026-10-08).
   */
  openDirectories?: OpenDirectories;
}

/** A closed directory drawn as a box: a pseudo-node with no symbols, standing in for everything under it. */
export interface ClosedDirectory {
  path: string;
  name: string;
  /** The files under it, at any depth. */
  files: number;
  /** Its immediate subdirectories. */
  directories: number;
}

/** A drawn directory's state and what it does not draw: its own files, and its immediate subdirectories, not in the picture. */
export interface DirectoryDisclosure {
  state: "encasing" | "open";
  hiddenFiles: number;
  hiddenDirectories: number;
}

/**
 * A disclosed exploration before its order: the retained files and the references between them, ranked into columns,
 * with everything the order step takes but its start, so that the order step can be run from several starts.
 */
export interface Exploration {
  subgraph: LocalSubgraph;
  ranking: BranchRanking;
  hiddenConnections: Map<string, number>;
  relevantSymbols: Map<string, Set<string>>;
  /** The files drawn compact and unwired because their directory is open; their references are hidden connections. */
  members: Set<string>;
  /** The closed directories drawn as boxes, by path. */
  closed: Map<string, ClosedDirectory>;
  /** Every directory the picture may draw as a membrane, by path: its state and what it hides. */
  directories: Map<string, DirectoryDisclosure>;
  /** What the order step takes, its sweeps and start apart. */
  order: Omit<OrderInput, "sweeps" | "seed" | "start">;
}

/** The archetype a closed directory's pseudo-node carries, which no file has. */
const DIRECTORY_ARCHETYPE = "directory";

/** A closed directory as a node of the picture: its path for an id, no symbols, no references. */
export function closedDirectoryNode(closed: ClosedDirectory): ExplorerNodePayload {
  return { id: closed.path, name: closed.name, codePath: closed.path, codeRelativePath: closed.path, docPath: "", docRelativePath: "",
    archetype: DIRECTORY_ARCHETYPE, dependencies: [], dependents: [], missingDependencies: [], publicSymbols: [] };
}

/** Whether a node of the picture is a closed directory's box rather than a file's card. */
export const isClosedDirectory = (node: ExplorerNodePayload): boolean => node.archetype === DIRECTORY_ARCHETYPE;

/** What every directory holds, from the graph's files: the files directly in it and its immediate subdirectories, each by path. */
export function directoryIndex(nodes: readonly ExplorerNodePayload[]): Map<string, { files: string[]; subdirectories: string[] }> {
  const index = new Map<string, { files: string[]; subdirectories: string[] }>();
  const entry = (directory: string): { files: string[]; subdirectories: string[] } =>
    index.get(directory) ?? index.set(directory, { files: [], subdirectories: [] }).get(directory)!;
  for (const node of nodes) {
    let child = parentDirectory(node.codeRelativePath);
    entry(child).files.push(node.id);
    while (child !== "") {
      const parent = parentDirectory(child);
      const siblings = entry(parent).subdirectories;
      if (!siblings.includes(child)) siblings.push(child);
      child = parent;
    }
  }
  for (const contents of index.values()) { contents.files.sort(); contents.subdirectories.sort(); }
  return index;
}

/**
 * The columns with every opened directory's unwired items laid in: the closed boxes and the compact files of an
 * opened directory fill a span of columns from an anchor, the first column of the party files under it, else its
 * nearest ancestor's, else the first column; as wide as the party files' span or the square root of the items'
 * count, whichever is more, item i in column anchor plus i modulo the width; columns past the party's are appended.
 * So a directory entered with nothing pinned is a grid about as wide as tall, and a directory opened beside pinned
 * files fills the room its own files already take.
 */
export function placeMembers(
  party: readonly (readonly string[])[],
  opened: OpenDirectories,
  itemsOf: (directory: string) => readonly string[],
  directoryOf: (id: string) => string
): string[][] {
  const columns = party.map(column => [...column]);
  const partyColumns = (directory: string): number[] =>
    party.flatMap((column, index) => (column.some(id => under(directoryOf(id), directory)) ? [index] : []));
  const anchorOf = (directory: string): number => {
    for (let current: string | null = directory; current !== null; current = current === "" ? null : parentDirectory(current)) {
      const held = partyColumns(current);
      if (held.length) return Math.min(...held);
    }
    return 0;
  };
  for (const directory of [...opened].sort()) {
    const items = itemsOf(directory);
    if (!items.length) continue;
    const held = partyColumns(directory);
    const span = held.length ? Math.max(...held) - Math.min(...held) + 1 : 0;
    const width = Math.max(span, Math.ceil(Math.sqrt(items.length)));
    const anchor = anchorOf(directory);
    items.forEach((item, index) => {
      const column = anchor + (index % width);
      while (columns.length <= column) columns.push([]);
      columns[column].push(item);
    });
  }
  return columns;
}

/** One reference's identity: its two files, its two symbols and its kind. */
export function edgeKey(edge: LocalEdge): string {
  return JSON.stringify([edge.sourceId, edge.targetId, edge.sourceSymbol, edge.targetSymbol, edge.kind]);
}

/**
 * Disclose the union of independent pins. Once both endpoints are present,
 * retain their relationship even when neither pin directly requested it.
 * Filters hide neighbors, but never the selected or explicitly pinned files.
 * The options set the layout's dials: the symbol order says how a card's rows
 * stand, by where their wires lead (the layout's choice), alphabetically, or as
 * the Live Doc lists them; the ranking's pull and tie rule and the order's
 * sweeps and start are the layout lab's levers. The exploration and its order
 * are two steps, `exploreBranches` and `orderExploration`, so that the order
 * step, the layout's one inexact step, can be run from several starts over
 * one exploration; this runs both once.
 */
export function buildBranches(
  center: ExplorerNodePayload | null,
  graph: ExplorerGraphPayload,
  pins: PinSet,
  include: (node: ExplorerNodePayload) => boolean,
  options: BranchOptions = {}
): BranchGraph {
  return orderExploration(exploreBranches(center, graph, pins, include, options), options.order);
}

/**
 * The exploration's first two steps: the retained files and references disclosed, the opened directories' contents
 * joined as compact files and closed boxes, and everything ranked into columns. The center, the file in focus, is
 * disclosed whole while it holds no pin of its own, so that pinning elsewhere expands the picture without losing
 * it (2026-10-15); a picture entered by directory has no center.
 */
export function exploreBranches(
  center: ExplorerNodePayload | null,
  graph: ExplorerGraphPayload,
  pins: PinSet,
  include: (node: ExplorerNodePayload) => boolean,
  options: Pick<BranchOptions, "symbolOrder" | "ranking" | "membraneDepth" | "openDirectories" | "path"> = {}
): Exploration {
  const symbolOrder = options.symbolOrder ?? "layout";
  const id = (endpoint: string | { id: string }): string => typeof endpoint === "string" ? endpoint : endpoint.id;
  const byId = new Map(graph.nodes.map(node => [node.id, node]));
  const path = options.path;
  const pathGraph = path ? buildPathSubgraph(path.nodeIds, graph, id, nodeId => byId.get(nodeId)) : null;
  const pathColumns = path ? new Map((pathGraph?.nodes ?? []).map((node, column) => [node.id, column])) : null;
  // An empty exploration is a browseable scan root, not a hidden directory pin.
  const rootLanding = !path && !center && !pins.entries.length && !options.openDirectories?.size;
  const opened = path ? NO_OPEN_DIRECTORIES : rootLanding ? new Set([""]) : options.openDirectories ?? NO_OPEN_DIRECTORIES;
  // A direct file link discloses its neighborhood without manufacturing persistent pins, and keeps it while pins
  // elsewhere grow the picture; the center's own pins, once it has any, say what of it is disclosed.
  const centerPinned = !!center && pins.entries.some(pin => pin.nodeId === center.id);
  const disclosure = path ? { entries: [] } : center && !centerPinned ? retainFile(pins, center.id) : pins;
  const retained = path ? new Set(pathColumns!.keys())
    : new Set([...(center ? [center.id] : []), ...pins.entries.map(pin => pin.nodeId)]);
  const relevantSymbols = new Map<string, Set<string>>();
  const remember = (nodeId: string, symbol?: string): void => {
    const rows = relevantSymbols.get(nodeId) ?? new Set<string>();
    rows.add(symbol === "*" ? "*" : normalizeSymbolIdentifier(symbol) ?? "__internals__");
    relevantSymbols.set(nodeId, rows);
  };
  for (const pin of disclosure.entries) remember(pin.nodeId, pin.symbol);
  for (const { link } of getVisibleConnections(disclosure, graph.links)) {
    remember(id(link.source), link.sourceSymbol);
    remember(id(link.target), link.targetSymbol);
    for (const endpoint of [id(link.source), id(link.target)]) {
      const node = byId.get(endpoint);
      if (node && include(node)) retained.add(endpoint);
    }
  }
  // The opened directories' contents: each file directly in one, compact and unwired unless it is retained; each
  // immediate subdirectory neither opened nor encasing, a closed box.
  const index = directoryIndex(graph.nodes);
  const members = new Set<string>();
  const closed = new Map<string, ClosedDirectory>();
  for (const directory of [...opened].sort()) {
    const contents = index.get(directory);
    if (!contents) continue;
    for (const file of contents.files) {
      const node = byId.get(file);
      if (node && !retained.has(file) && include(node)) members.add(file);
    }
    for (const subdirectory of contents.subdirectories) {
      if (directoryState(subdirectory, opened, retained) === "closed") closed.set(subdirectory, closedDirectory(subdirectory, index));
    }
  }
  const files = [...retained, ...members].flatMap(key => byId.get(key) ?? []);
  const boxes = [...closed.values()].map(closedDirectoryNode);
  const nodes = [...files, ...boxes];
  for (const box of boxes) byId.set(box.id, box);
  const shown = new Set(nodes.map(node => node.id));
  const links: LocalEdge[] = [];
  const hiddenConnections = new Map<string, number>();
  for (const link of graph.links) {
    const sourceId = id(link.source), targetId = id(link.target);
    const followsPath = !pathColumns || pathColumns.get(sourceId) === (pathColumns.get(targetId) ?? -2) + 1;
    if (retained.has(sourceId) && retained.has(targetId) && followsPath) {
      links.push({ sourceId, targetId, sourceSymbol: link.sourceSymbol, targetSymbol: link.targetSymbol,
        kind: link.kind, referenceCount: link.referenceCount, direction: !path && sourceId === center?.id ? "outbound" : "inbound" });
      if (path) { remember(sourceId, link.sourceSymbol); remember(targetId, link.targetSymbol); }
    } else {
      for (const endpoint of new Set([sourceId, targetId])) {
        if (shown.has(endpoint)) hiddenConnections.set(endpoint, (hiddenConnections.get(endpoint) ?? 0) + 1);
      }
    }
  }
  const keys = new Set(links.map(edgeKey));
  const party = files.filter(node => retained.has(node.id));
  const row = (symbol: string | undefined): string => normalizeSymbolIdentifier(symbol) ?? "__internals__";
  // A file's own references. Each one that touches a pinned symbol discloses the row at its other end, as a visible
  // connection between files discloses its far row: one hop either way, from the pinned rows only, so that a disclosed
  // partner discloses nothing of its own (the owner's question, 2026-11-03).
  const pinnedRows = new Map<string, Set<string>>();
  for (const pin of disclosure.entries) {
    if (pin.symbol === "*") continue;
    (pinnedRows.get(pin.nodeId) ?? pinnedRows.set(pin.nodeId, new Set()).get(pin.nodeId)!).add(row(pin.symbol));
  }
  for (const node of path ? [] : party) for (const edge of buildSelfLoopEdges(node)) {
    if (!keys.has(edgeKey(edge))) { links.push(edge); keys.add(edgeKey(edge)); }
    const pinned = pinnedRows.get(node.id);
    if (pinned?.has(row(edge.sourceSymbol))) remember(node.id, edge.targetSymbol);
    if (pinned?.has(row(edge.targetSymbol))) remember(node.id, edge.sourceSymbol);
  }
  const directoryOfNode = (nodeId: string): string => parentDirectory(byId.get(nodeId)?.codeRelativePath ?? nodeId);
  const ranked = rankBranches(party, links, options.ranking);
  const columns = placeMembers(ranked.columns.map(column => column.map(node => node.id)), opened, directory => {
    const contents = index.get(directory);
    const inside = (path: string): boolean => parentDirectory(path) === directory;
    return [...[...closed.keys()].filter(inside), ...(contents?.files ?? []).filter(file => members.has(file))];
  }, directoryOfNode);
  const ranking: BranchRanking = { columns: columns.map(column => column.flatMap(nodeId => byId.get(nodeId) ?? [])), back: ranked.back };
  if (path?.fromSymbol && pathGraph) remember(pathGraph.nodes[0].id, path.fromSymbol);
  if (path?.toSymbol && pathGraph) remember(pathGraph.nodes[pathGraph.nodes.length - 1].id, path.toSymbol);
  const rows = visibleRows(files, path ? null : center?.id ?? null, relevantSymbols, symbolOrder);
  for (const box of boxes) rows.set(box.id, []);
  // Every directory the picture may draw as a membrane: an ancestor of something shown, the scan root aside.
  const directories = new Map<string, DirectoryDisclosure>();
  for (const node of nodes) {
    for (let directory = parentDirectory(node.codeRelativePath); directory !== ""; directory = parentDirectory(directory)) {
      if (directories.has(directory)) continue;
      const contents = index.get(directory);
      directories.set(directory, {
        state: opened.has(directory) ? "open" : "encasing",
        hiddenFiles: (contents?.files ?? []).filter(file => !shown.has(file)).length,
        hiddenDirectories: (contents?.subdirectories ?? []).filter(subdirectory => ![...shown].some(path => under(path, subdirectory))).length
      });
    }
  }
  const forward: ForwardReference[] = links
    .filter(edge => edge.sourceId !== edge.targetId && !ranking.back.has(edgeKey(edge)))
    .map(edge => ({ key: edgeKey(edge), provider: edge.targetId, consumer: edge.sourceId,
      pin: `${edge.targetId}\0${row(edge.targetSymbol)}`, providerRow: row(edge.targetSymbol), consumerRow: row(edge.sourceSymbol) }));
  // A file's own references, which the ordering uses to break ties among its rows.
  const internal = new Map<string, [string, string][]>();
  for (const edge of links) {
    if (edge.sourceId !== edge.targetId) continue;
    (internal.get(edge.sourceId) ?? internal.set(edge.sourceId, []).get(edge.sourceId)!).push([row(edge.targetSymbol), row(edge.sourceSymbol)]);
  }
  const root = commonDirectory(nodes.map(node => parentDirectory(node.codeRelativePath)));
  return {
    subgraph: { center: path ? pathGraph?.center ?? null : center, nodes, links, inboundIds: new Set(), outboundIds: new Set() },
    ranking,
    hiddenConnections,
    relevantSymbols,
    members,
    closed,
    directories,
    order: {
      columns,
      directoryOf: nodeId => membraneDirectory(directoryOfNode(nodeId), root, options.membraneDepth ?? null),
      rows,
      // Only the layout order moves rows, and Internals keeps the foot of the card.
      movable: symbolOrder === "layout" ? name => name !== "__internals__" : undefined,
      edges: forward,
      internal
    }
  };
}

/** The exploration's third step: its columns ordered from the given start, with the lanes and each card's rows the order chose. */
export function orderExploration(exploration: Exploration, options: OrderOptions = {}): BranchGraph {
  const byId = new Map(exploration.subgraph.nodes.map(node => [node.id, node]));
  const order = orderBranches({ ...exploration.order, sweeps: options.sweeps, seed: options.seed, start: options.start });
  return {
    subgraph: exploration.subgraph,
    columns: order.columns.map(ids => ids.flatMap(nodeId => byId.get(nodeId) ?? [])),
    hiddenConnections: exploration.hiddenConnections,
    relevantSymbols: exploration.relevantSymbols,
    back: exploration.ranking.back,
    rows: order.rows,
    order
  };
}

/** A closed directory's box as the picture counts it: the files under it at any depth, and its immediate subdirectories. */
function closedDirectory(path: string, index: ReadonlyMap<string, { files: string[]; subdirectories: string[] }>): ClosedDirectory {
  let files = 0;
  for (const [directory, contents] of index) if (under(directory, path)) files += contents.files.length;
  return { path, name: path.slice(path.lastIndexOf("/") + 1), files, directories: index.get(path)?.subdirectories.length ?? 0 };
}

/** The deepest directory every given directory is in or under; "" when they share none. */
export function commonDirectory(directories: readonly string[]): string {
  if (directories.length === 0) return "";
  let common = directories[0].split("/").filter(Boolean);
  for (const directory of directories.slice(1)) {
    const parts = directory.split("/").filter(Boolean);
    let shared = 0;
    while (shared < common.length && shared < parts.length && common[shared] === parts[shared]) shared++;
    common = common.slice(0, shared);
    if (common.length === 0) break;
  }
  return common.join("/");
}

/**
 * The membrane a file's directory belongs to when only `depth` levels below the common directory are membranes: the
 * directory cut to that many levels below the root, the root itself at zero, the directory as it is when depth is null.
 */
export function membraneDirectory(directory: string, root: string, depth: number | null): string {
  if (depth === null) return directory;
  const below = directory === root ? [] : (root === "" ? directory : directory.startsWith(`${root}/`) ? directory.slice(root.length + 1) : "").split("/").filter(Boolean);
  return [...(root ? [root] : []), ...below.slice(0, Math.max(0, Math.floor(depth)))].join("/");
}

/**
 * The rows each card will show, top to bottom, by their normalized names: every
 * row of a file retained whole or in focus, otherwise the rows some pin needs,
 * with Internals last; alphabetical when that order is chosen, otherwise as
 * the Live Doc lists them, which the layout order then moves by the wires. A
 * compact member of an opened directory shows no row.
 */
function visibleRows(nodes: readonly ExplorerNodePayload[], centerId: string | null, relevant: ReadonlyMap<string, ReadonlySet<string>>, symbolOrder: SymbolOrder): Map<string, string[]> {
  const rows = new Map<string, string[]>();
  for (const node of nodes) {
    const needed = relevant.get(node.id);
    const all = node.id === centerId || needed?.has("*");
    const names = node.publicSymbols.map(symbol => normalizeSymbolIdentifier(symbol) ?? symbol);
    const shown = all ? names : names.filter(name => needed?.has(name));
    if (symbolOrder === "alphabetical") shown.sort(compareSymbolNames);
    if (all || needed?.has("__internals__")) shown.push("__internals__");
    rows.set(node.id, shown);
  }
  return rows;
}

/** Alphabetical order of symbol names, case first set aside, then as the names compare. */
export function compareSymbolNames(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" }) || a.localeCompare(b);
}

/**
 * Rank providers before consumers so that the references cross the fewest
 * columns in all: every forward reference costs the columns it spans, and the
 * columns are the exact minimum of that sum, found by the same network simplex
 * that places the cards (Gansner, Koutsofios, North and Vo, section 2). A file
 * that could stand in several columns at the same cost takes the one with the
 * fewest cards, the rightmost among equals; a file nothing retained uses
 * stands in the last column, and so does every file no reference reaches. A
 * cycle is broken at the references that read backward in a provider-first
 * order of its members, so that every other reference flows left to right;
 * the broken ones are returned as `back`.
 */
export function rankBranches(nodes: ExplorerNodePayload[], links: LocalEdge[], options: RankingOptions = {}): BranchRanking {
  const pull = Math.max(0, options.pull ?? 0);
  const tie = options.tie ?? "fewest";
  const ids = nodes.map(node => node.id);
  const present = new Set(ids);
  const edges = links.filter(edge => edge.sourceId !== edge.targetId && present.has(edge.sourceId) && present.has(edge.targetId));
  // A link runs from the file that depends (its source) to the file it depends on (its target); the picture reads provider to consumer.
  const successors = new Map(ids.map(id => [id, new Set<string>()]));
  for (const edge of edges) successors.get(edge.targetId)!.add(edge.sourceId);
  const index = new Map<string, number>(), low = new Map<string, number>();
  const stack: string[] = [], onStack = new Set<string>(), components: string[][] = [];
  const visit = (id: string): void => {
    index.set(id, index.size); low.set(id, index.get(id)!); stack.push(id); onStack.add(id);
    for (const next of successors.get(id)!) {
      if (!index.has(next)) { visit(next); low.set(id, Math.min(low.get(id)!, low.get(next)!)); }
      else if (onStack.has(next)) low.set(id, Math.min(low.get(id)!, index.get(next)!));
    }
    if (low.get(id) === index.get(id)) {
      const component: string[] = [];
      let member: string;
      do { member = stack.pop()!; onStack.delete(member); component.push(member); } while (member !== id);
      components.push(component);
    }
  };
  for (const id of [...ids].sort()) if (!index.has(id)) visit(id);
  const componentOf = new Map(components.flatMap((members, i) => members.map(id => [id, i] as const)));
  // Within a cycle, members stand provider-first, by Eades, Lin and Smyth's order: a file nothing in the cycle
  // serves goes to the front, one that serves nothing in it goes to the back, and otherwise the one that
  // serves the most more than it uses; what is left after each choice is looked at again.
  const rank = new Map<string, number>();
  for (const members of components) {
    const remaining = new Set(members);
    const serves = (id: string): number => [...successors.get(id)!].filter(consumer => remaining.has(consumer)).length;
    const uses = (id: string): number => [...remaining].filter(other => successors.get(other)!.has(id)).length;
    const front: string[] = [], back: string[] = [];
    while (remaining.size) {
      const sorted = [...remaining].sort();
      const sink = sorted.find(id => serves(id) === 0);
      if (sink) { back.unshift(sink); remaining.delete(sink); continue; }
      const source = sorted.find(id => uses(id) === 0);
      if (source) { front.push(source); remaining.delete(source); continue; }
      const choice = sorted.reduce((best, id) => serves(id) - uses(id) > serves(best) - uses(best) ? id : best);
      front.push(choice);
      remaining.delete(choice);
    }
    [...front, ...back].forEach((id, i) => rank.set(id, i));
  }
  const back = new Set<string>();
  const forward = new Map(ids.map(id => [id, new Set<string>()]));
  for (const edge of edges) {
    const provider = edge.targetId, consumer = edge.sourceId;
    if (componentOf.get(provider) === componentOf.get(consumer) && rank.get(provider)! > rank.get(consumer)!) back.add(edgeKey(edge));
    else forward.get(provider)!.add(consumer);
  }
  // The columns: each forward reference, weighted by the references between its two files, costs the columns it
  // spans, and the solver finds the least total. Every file is a node; a reference is a constraint of at least one column.
  const at = new Map(ids.map((id, i) => [id, i]));
  const weights = new Map<string, number>();
  for (const edge of edges) {
    if (back.has(edgeKey(edge))) continue;
    const pair = `${edge.targetId}\0${edge.sourceId}`;
    weights.set(pair, (weights.get(pair) ?? 0) + 1);
  }
  const pairs: Constraint[] = [...weights].sort(([x], [y]) => x.localeCompare(y)).map(([pair, weight]) => {
    const [provider, consumer] = pair.split("\0");
    return { tail: at.get(provider)!, head: at.get(consumer)!, delta: 1, weight };
  });
  // The pull: a column past every file, toward which each file is drawn by the pull's weight, so that a file stands
  // further right than the spans alone would have it wherever the pull outweighs the spans it lengthens.
  const sink = ids.length;
  const pulls: Constraint[] = pull > 0 ? ids.map((_, v) => ({ tail: v, head: sink, delta: 0, weight: pull })) : [];
  const column = rankByNetworkSimplex(ids.length + (pull > 0 ? 1 : 0), [...pairs, ...pulls]).position.slice(0, ids.length);
  // Files no reference joins stand apart from one another, so each connected group ends at the last column, as a
  // file nothing uses does: the picture's right edge is where the chains end.
  const group = ids.map((_, i) => i);
  const find = (v: number): number => (group[v] === v ? v : (group[v] = find(group[v])));
  for (const edge of pairs) group[find(edge.tail)] = find(edge.head);
  const last = Math.max(0, ...column);
  const groupLast = new Map<number, number>();
  for (let v = 0; v < ids.length; v++) groupLast.set(find(v), Math.max(groupLast.get(find(v)) ?? 0, column[v]));
  for (let v = 0; v < ids.length; v++) column[v] += last - groupLast.get(find(v))!;
  // A file whose references weigh the same on both sides costs the same in any column between its providers and its
  // consumers (the pull counted on the consumers' side); by the tie rule it takes the column with the fewest other
  // cards, the rightmost among equals, so no stack grows for nothing, or the rightmost or leftmost outright.
  const cards = new Map<number, number>();
  for (const value of column) cards.set(value, (cards.get(value) ?? 0) + 1);
  for (let v = 0; v < ids.length; v++) {
    let inbound = 0, outbound = pull, low = 0, high = last;
    for (const edge of pairs) {
      if (edge.head === v) { inbound += edge.weight; low = Math.max(low, column[edge.tail] + 1); }
      if (edge.tail === v) { outbound += edge.weight; high = Math.min(high, column[edge.head] - 1); }
    }
    if (!inbound || inbound !== outbound) continue;
    let best = column[v];
    const others = (value: number): number => (cards.get(value) ?? 0) - (value === column[v] ? 1 : 0);
    if (tie === "right") best = high;
    else if (tie === "left") best = low;
    else for (let value = low; value <= high; value++) if (others(value) <= others(best)) best = value;
    cards.set(column[v], cards.get(column[v])! - 1);
    cards.set(best, (cards.get(best) ?? 0) + 1);
    column[v] = best;
  }
  const used = [...new Set(column)].sort((a, b) => a - b);
  const columns: ExplorerNodePayload[][] = used.map(() => []);
  nodes.forEach((node, i) => columns[used.indexOf(column[i])].push(node));
  for (const list of columns) list.sort((a, b) => a.codeRelativePath.localeCompare(b.codeRelativePath));
  return { columns, back };
}
